/**
 * ?scherm= (het scherm achter een tegel, ontwerp v4) is een laag van de
 * planroute alleen: laag-url.ts kent hem niet, en Vandaag hoeft er niets van
 * te weten. Een scherm erbij of eraf leest het plan niet opnieuw; voor de
 * rest geldt laagHervalideren, vergeleken zonder scherm. De root laat bij
 * een schermwissel zijn loader ook staan (rootHervalideren).
 *
 * ?reis=terug (de vraag "Hoe ga je naar huis?" vanaf Vandaag) telt net zo:
 * het paneel haalt hem eraf zodra de lade open is, en dat leest niets
 * opnieuw.
 */
import { type ShouldRevalidateFunctionArgs } from 'react-router'
import { LAAG_PARAMS, laagHervalideren } from '#app/utils/laag-url.ts'

type Args = Pick<
	ShouldRevalidateFunctionArgs,
	'currentUrl' | 'nextUrl' | 'formMethod' | 'defaultShouldRevalidate'
>

const LAAG: ReadonlySet<string> = new Set(LAAG_PARAMS)
/** Van de planroute alleen, en zonder iets te lezen: het scherm en de
 *  eenmalige vraag vanaf Vandaag. */
const EIGEN = ['scherm', 'reis'] as const

function zonderScherm(url: URL): URL {
	const kaal = new URL(url)
	for (const key of EIGEN) kaal.searchParams.delete(key)
	return kaal
}

function waarden(params: URLSearchParams, key: string) {
	return params.getAll(key).join('\u0000')
}

/**
 * Gaat er alleen een scherm open of dicht? Zelfde pad, geen actie, ?scherm=
 * anders, en wat er verder verschilt is een laag (Meer gaat dicht als
 * Kaartjes en plekken zijn scherm opent).
 */
export function alleenSchermWissel(args: Args): boolean {
	const { currentUrl: a, nextUrl: b } = args
	if (args.formMethod || a.pathname !== b.pathname) return false
	if (
		EIGEN.every(
			(key) => waarden(a.searchParams, key) === waarden(b.searchParams, key),
		)
	)
		return false
	const sleutels = new Set([...a.searchParams.keys(), ...b.searchParams.keys()])
	for (const key of sleutels) {
		if ((EIGEN as readonly string[]).includes(key) || LAAG.has(key)) continue
		if (waarden(a.searchParams, key) !== waarden(b.searchParams, key))
			return false
	}
	return true
}

export function planHervalideren(args: Args): boolean {
	const van = zonderScherm(args.currentUrl)
	const naar = zonderScherm(args.nextUrl)
	if (
		!args.formMethod &&
		van.href === naar.href &&
		args.currentUrl.href !== args.nextUrl.href
	) {
		return false
	}
	return laagHervalideren({ ...args, currentUrl: van, nextUrl: naar })
}

/** Gaat alleen het hele scherm van de lade (?groot=1) aan of uit? Zelfde
 *  pad, geen actie, en alleen groot verschilt. Ook als Terug in de browser
 *  (een POP, zonder defaultShouldRevalidate) het hele scherm sluit. */
function alleenGrootWissel(args: Args): boolean {
	const { currentUrl: a, nextUrl: b } = args
	if (args.formMethod || a.pathname !== b.pathname) return false
	const sleutels = new Set([...a.searchParams.keys(), ...b.searchParams.keys()])
	let anders = false
	for (const key of sleutels) {
		if (waarden(a.searchParams, key) === waarden(b.searchParams, key)) continue
		if (key !== 'groot') return false
		anders = true
	}
	return anders
}

/** shouldRevalidate van de root: een scherm openen of sluiten, of de lade
 *  over het hele scherm zetten, haalt niets nieuws op; al het andere volgt
 *  React Router. */
export function rootHervalideren(args: Args): boolean {
	if (alleenSchermWissel(args) || alleenGrootWissel(args)) return false
	return args.defaultShouldRevalidate
}
